import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from '@/hooks/useUserRole';
import { useEffect, useState } from 'react';
import { mapToRole, canCompose, canListMarketplace, canModerate, Session, ReasonCode } from '@/lib/auth';

// Helper to log authorization failures to Supabase
const logAuthzFailure = async (
  userId: string | undefined,
  route: string,
  method: string,
  reasonCode: ReasonCode,
  communityKey?: string
) => {
  try {
    await supabase.rpc('log_authz_failure', {
      p_user_id: userId || null,
      p_route: route,
      p_method: method,
      p_reason_code: reasonCode,
      p_community_key: communityKey || null,
      p_ip_address: null, // Browser doesn't have access to IP
      p_user_agent: navigator.userAgent
    });
  } catch (error) {
    console.error('Failed to log authz failure:', error);
  }
};

function useSession(): Session | null {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const { roles, loading } = useUserRole();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading || isAuthenticated === null) return null;

  const role = mapToRole(isAuthenticated, roles);
  
  return {
    role,
    isAuthenticated,
    roles,
    community_key: undefined, // Can be extended later
    modsOf: [], // Can be extended later with community moderator mappings
  };
}

export function RequireVerified({ children }: { children: JSX.Element }) {
  const session = useSession();
  const location = useLocation();
  const [logged, setLogged] = useState(false);

  useEffect(() => {
    if (session && !canCompose(session) && !logged) {
      logAuthzFailure(
        session.isAuthenticated ? undefined : 'guest',
        location.pathname,
        'GET',
        'auth_required'
      );
      setLogged(true);
    }
  }, [session, location.pathname, logged]);

  if (!session) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return canCompose(session) 
    ? children 
    : <Navigate 
        to={`/?r=auth_required&next=${encodeURIComponent(location.pathname)}`} 
        replace 
      />;
}

export function RequireGreenCheck({ children }: { children: JSX.Element }) {
  const session = useSession();
  const location = useLocation();
  const [logged, setLogged] = useState(false);

  useEffect(() => {
    if (session && !canListMarketplace(session) && !logged) {
      logAuthzFailure(
        session.isAuthenticated ? undefined : 'guest',
        location.pathname,
        'GET',
        'green_check_required'
      );
      setLogged(true);
    }
  }, [session, location.pathname, logged]);

  if (!session) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return canListMarketplace(session) 
    ? children 
    : <Navigate 
        to={`/?r=green_check_required&next=${encodeURIComponent(location.pathname)}`} 
        replace 
      />;
}

export function RequireModerator({ children }: { children: JSX.Element }) {
  const session = useSession();
  const location = useLocation();
  const [logged, setLogged] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const ck = searchParams.get('community_key') || session.community_key || '';

  useEffect(() => {
    if (session && !canModerate(session, ck) && !logged) {
      logAuthzFailure(
        session.isAuthenticated ? undefined : 'guest',
        location.pathname,
        'GET',
        'mod_scope_required',
        ck
      );
      setLogged(true);
    }
  }, [session, location.pathname, ck, logged]);

  if (!session) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }
  
  return canModerate(session, ck) 
    ? children 
    : <Navigate 
        to={`/?r=mod_scope_required`} 
        replace 
      />;
}
